/**
 * Lo que el bot sabe, y cómo se lo enseña (GKTIBO-69, 87, 88).
 *
 * Vive aparte de `api.ts` porque son nueve endpoints con su porqué —el grupo
 * más grande del cliente— y juntos empujaban ese archivo por encima de las 300
 * líneas (D4). Separarlo también deja claro qué se toca cuando cambia el alta:
 * esto, y nada más.
 *
 * Se mezcla dentro de `api.tenants`, así que quien lo usa no cambia.
 */

import type { MotivoDeDescarte, PaginaLeidaResumen } from '@chatbot/shared-types';
import { request } from './peticion.ts';

export const conocimiento = {
  getKb: (id: string) => request<{ markdown: string }>(`/admin/tenants/${id}/kb`),

  /**
   * Lo que el bot sabe, por temas (GKTIBO-69).
   *
   * **No es `getKb` con otro nombre.** `getKb` devuelve el `.md` crudo, que
   * no se le muestra a nadie: el dueño no es técnico y el conocimiento se
   * construye conversando. Acá viene ya convertido en temas, con los
   * ejemplos de la plantilla afuera — si viajaran, el dueño los leería como
   * los precios de su negocio (G3, L27).
   */
  temasDelConocimiento: (id: string) =>
    request<{ temas: import('../pages/alta/PanelDeConocimiento.tsx').TemaDelPanel[] }>(
      `/admin/tenants/${id}/conocimiento`,
    ),
  updateKb: (id: string, markdown: string) =>
    request<{ success: boolean }>(`/admin/tenants/${id}/kb`, {
      method: 'PUT',
      body: JSON.stringify({ markdown }),
    }),
  getKbStatus: (id: string) =>
    request<{
      status: 'synced' | 'not_synced';
      data?: { s3_version: string; chunks_count: number; updated_at: string };
    }>(`/admin/tenants/${id}/kb/status`),
  /**
   * Siembra la base de conocimiento. Idempotente: 409 si ya existía.
   *
   * `dueno` cambia la plantilla entera: la nuestra habla de «SystemPrompt» y
   * «prompt caching», la del dueño le pregunta qué NO hace su negocio.
   */
  /**
   * Encola el rastreo de la página del negocio (GKTIBO-65).
   *
   * **Devuelve `jobId`, no el resultado**: leer veinte páginas de un sitio
   * ajeno tarda más que el timeout del endpoint. El avance se consulta con
   * `avanceDeIngesta`.
   */
  leerMiPagina: (id: string, url: string, urls?: string[]) =>
    request<{ jobId: string; estado: string }>(`/admin/tenants/${id}/conocimiento/web`, {
      method: 'POST',
      // `urls`: «Leer estas también» (GKTIBO-136). Sólo viaja si pidió.
      body: JSON.stringify(urls && urls.length > 0 ? { url, urls } : { url }),
    }),

  avanceDeIngesta: (id: string, jobId: string) =>
    request<{
      jobId: string;
      estado: 'rastreando' | 'terminado' | 'fallido';
      paginasLeidas: number;
      fallas: Array<{ url: string; motivo: string; detalle?: string }>;
      motivo?: string;
      truncado: boolean;
      /** Cuáles se leyeron y cuáles se dejaron afuera, con motivo (GKTIBO-135). */
      leidas: PaginaLeidaResumen[];
      descartadas: Array<{ url: string; motivo: MotivoDeDescarte }>;
      /** El rastreo terminó pero el conocimiento no se publicó, y por qué. */
      conocimientoSinPublicar?: string;
    }>(`/admin/tenants/${id}/conocimiento/web/${jobId}`),

  /**
   * Un turno de la conversación del alta (GKTIBO-88).
   *
   * **Propone, no guarda.** Lo que queda como conocimiento del bot lo decide
   * el dueño tocando «Guardar», y eso va por `cerrarTema`. Es la capa que
   * impide que una invención del modelo llegue al bot en silencio.
   */
  conversar: (id: string, mensaje: string) =>
    request<{
      propuestas: Array<{ tema: string; texto: string; seDescartoLoInventado: boolean }>;
      repregunta: string | null;
      temaEnCurso: string | null;
      /** El modelo no se pudo usar: lo que vuelve es el texto del dueño. */
      sinAsistente?: boolean;
    }>(`/admin/tenants/${id}/conocimiento/conversacion`, {
      method: 'POST',
      body: JSON.stringify({ mensaje }),
    }),

  /**
   * Con qué arranca la conversación: el tema que toca preguntar primero.
   *
   * Sin esto Tibo prometía preguntas y se quedaba callado, y lo primero que
   * el dueño escribiera se guardaba como si fuera una respuesta.
   */
  aperturaDeConversacion: (id: string) =>
    request<{
      temaEnCurso: string | null;
      /**
       * Terminada la agenda, lo que el sistema quiere preguntar (GKTIBO-148):
       * una respuesta de la prueba que salió de ningún lado, un agujero
       * medido, dos hechos que se contradicen.
       */
      pregunta?: { id: string; motivo: string; texto: string; hechos?: string[] } | null;
    }>(`/admin/tenants/${id}/conocimiento/conversacion`),

  /** La pregunta ya se contestó (o el dueño la descartó): sale de la cola. */
  resolverPregunta: (
    id: string,
    preguntaId: string,
    body: { descartar?: boolean; gana?: string } = {},
  ) =>
    request<{ ok: boolean }>(`/admin/tenants/${id}/conocimiento/preguntas/${preguntaId}/resolver`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Cómo suena el bot (GKTIBO-122).
   *
   * Ruta propia porque la voz llega ESTRUCTURADA —nombre, tono, largo—, no
   * como texto: `cerrarTema` la rechaza a propósito.
   */
  guardarLaVoz: (id: string, voz: { nombre?: string; tono: string; largo: 'corto' | 'explica' }) =>
    request<{ ok: boolean }>(`/admin/tenants/${id}/conocimiento/voz`, {
      method: 'POST',
      body: JSON.stringify(voz),
    }),

  /** Escucharlo antes de guardarlo. No escribe nada. */
  muestraDeLaVoz: (
    id: string,
    voz: { nombre?: string; tono: string; largo: 'corto' | 'explica'; pregunta: string },
  ) =>
    request<{ respuesta: string }>(`/admin/tenants/${id}/conocimiento/voz/muestra`, {
      method: 'POST',
      body: JSON.stringify(voz),
    }),

  /** Acá SÍ se escribe el conocimiento del bot. Uno por tema, no por turno. */
  cerrarTema: (id: string, tema: string, texto: string) =>
    request<{ ok: boolean }>(`/admin/tenants/${id}/conocimiento/borrador/cerrar`, {
      method: 'POST',
      body: JSON.stringify({ tema, texto }),
    }),

  /**
   * «Otros» es una cola (GKTIBO-147): si varios hechos comparten asunto, Tibo
   * propone una sección. `null` cuando no hay nada que proponer.
   */
  propuestaDeSeccion: (id: string) =>
    request<{ propuesta: { tema: string; titulo: string; ids: string[] } | null }>(
      `/admin/tenants/${id}/conocimiento/propuesta-de-seccion`,
    ),

  /** Sí: los hechos pasan a su sección. No: no se vuelve a proponer. */
  contestarPropuestaDeSeccion: (
    id: string,
    body: { tema: string; ids: string[]; acepta: boolean },
  ) =>
    request<{ ok: boolean; agrupados: number }>(
      `/admin/tenants/${id}/conocimiento/propuesta-de-seccion`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  initKb: (id: string, dueno?: { nombre: string }) =>
    request<{ success: boolean; tenantId: string; key: string }>(
      dueno
        ? `/admin/tenants/${id}/kb/init?publico=dueno&nombre=${encodeURIComponent(dueno.nombre)}`
        : `/admin/tenants/${id}/kb/init`,
      { method: 'POST' },
    ),
};
